import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  MessageSquare, 
  Calendar, 
  Building2, 
  TrendingUp, 
  User, 
  Target,
  ArrowRight,
  Bot,
  FileText,
  Sparkles,
  CheckCircle2,
  UtensilsCrossed,
  Stethoscope,
  GraduationCap,
  Home,
  ShoppingCart,
  Scissors,
  Briefcase,
  BookOpen,
  Zap,
  CreditCard
} from 'lucide-react';

const Portfolio: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Most Popular Agents (shown first)
  const mostPopularAgents = [
    'support-genius',
    'auto-scheduler-pro',
    'content-creation-assistant'
  ];

  // Portfolio items with their data
  const portfolioItems = [
    {
      id: 'content-creation-assistant',
      translationKey: 'contentCreation',
      icon: FileText,
    },
    {
      id: 'support-genius',
      translationKey: 'supportGenius',
      icon: MessageSquare,
    },
    {
      id: 'auto-scheduler-pro',
      translationKey: 'autoScheduler',
      icon: Calendar,
    },
    {
      id: 'knowledge-hub-assistant',
      translationKey: 'knowledgeHub',
      icon: Building2,
    },
    {
      id: 'financial-insights-coach',
      translationKey: 'financialInsights',
      icon: TrendingUp,
    },
    {
      id: 'personal-productivity-partner',
      translationKey: 'personalProductivity',
      icon: User,
    },
    {
      id: 'lead-conversion-agent',
      translationKey: 'leadConversion',
      icon: Target,
    }
  ];

  // Industries served
  const industries = [
    { name: t('portfolio.industries.restaurants'), icon: UtensilsCrossed },
    { name: t('portfolio.industries.clinics'), icon: Stethoscope },
    { name: t('portfolio.industries.coaches'), icon: GraduationCap },
    { name: t('portfolio.industries.realEstate'), icon: Home },
    { name: t('portfolio.industries.ecommerce'), icon: ShoppingCart },
    { name: t('portfolio.industries.salons'), icon: Scissors },
    { name: t('portfolio.industries.consulting'), icon: Briefcase },
    { name: t('portfolio.industries.education'), icon: BookOpen }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <Header />

      {/* Hero Section */}
      <main className="relative pt-12 pb-8">
        <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <div className="text-center max-w-5xl mx-auto relative z-10">
            {/* Glowing badge */}
            <div className="inline-flex items-center px-4 py-2 bg-brand-primary/20 border border-brand-secondary/50 rounded-full mb-8 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-brand-secondary mr-2" />
              <span className="text-brand-secondary text-sm font-medium">{t('solutionsStore.hero.badge')}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 leading-[1.1]">
              <span className="text-white">{t('solutionsStore.hero.title')}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/80 mb-8 leading-relaxed font-light max-w-3xl mx-auto">
              {t('solutionsStore.hero.subtitle')}
            </p>

            {/* Value Proposition */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/10">
                <Zap className="w-5 h-5 text-brand-secondary" />
                <span className="text-white font-medium">{t('solutionsStore.hero.plugAndPlay')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/10">
                <CreditCard className="w-5 h-5 text-brand-secondary" />
                <span className="text-white font-medium">{t('solutionsStore.hero.fixedPrice')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/10">
                <Bot className="w-5 h-5 text-brand-secondary" />
                <span className="text-white font-medium">{t('solutionsStore.hero.instantActivation')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioItems.map((item) => {
              const IconComponent = item.icon;
              const isPopular = mostPopularAgents.includes(item.id);
              return (
                <Link
                  key={item.id}
                  to={`/solutions/${item.id}`}
                  className="group relative backdrop-blur-lg rounded-2xl border border-brand-secondary/30 bg-gradient-to-br from-brand-secondary/5 to-brand-primary/5 transition-all duration-300 hover:border-brand-secondary/50 hover:shadow-2xl hover:shadow-brand-secondary/20 hover:-translate-y-1"
                >
                  {isPopular && (
                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-brand-secondary to-brand-primary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 z-10">
                      <CheckCircle2 className="w-3 h-3" />
                      {t('portfolio.mostPopular.badge')}
                    </div>
                  )}
                  
                  <div className="p-6">
                    {/* Icon */}
                    <div className="relative w-14 h-14 bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:from-brand-secondary/30 group-hover:to-brand-primary/30 group-hover:scale-110">
                      <IconComponent className="w-7 h-7 text-brand-secondary" />
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-brand-secondary transition-colors">
                      {t(`portfolio.items.${item.translationKey}.title`)}
                    </h3>

                    {/* Tagline */}
                    <p className="text-brand-secondary text-sm font-semibold mb-3">
                      {t(`portfolio.items.${item.translationKey}.tagline`)}
                    </p>

                    {/* Pricing */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div>
                        <div className="text-white font-bold text-lg">€299</div>
                        <div className="text-white/60 text-xs">+ €149{t('solutionsStore.perMonth')}</div>
                      </div>
                      <div className="flex items-center text-brand-secondary font-medium text-sm group-hover:translate-x-1 transition-transform">
                        {t('solutionsStore.viewDetails')}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>

                  {/* Hover effect glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10"></div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Industries Served Section */}
        <section className="relative py-12">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-primary/10 to-transparent pointer-events-none"></div>
          
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                {t('portfolio.industries.title')}
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                {t('portfolio.industries.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-6">
              {industries.map((industry, idx) => {
                const IconComponent = industry.icon;
                return (
                  <div
                    key={idx}
                    className="group relative backdrop-blur-lg rounded-2xl p-6 border border-white/20 bg-gradient-to-br from-white/5 to-white/10 transition-all duration-300 hover:border-brand-secondary/50 hover:shadow-xl hover:shadow-brand-secondary/20 hover:-translate-y-1 text-center"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 mb-4 group-hover:from-brand-secondary/30 group-hover:to-brand-primary/30 transition-all duration-300">
                      <IconComponent className="w-8 h-8 text-brand-secondary" />
                    </div>
                    <h3 className="text-white font-semibold text-sm sm:text-base">
                      {industry.name}
                    </h3>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Custom Solution CTA Section */}
        <section className="relative py-12">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition duration-1000"></div>
              
              <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-10 border border-brand-secondary/30 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('solutionsStore.customCta.title')}
                </h2>
                <p className="text-white/70 text-lg mb-6">
                  {t('solutionsStore.customCta.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  <div className="text-white/60 text-sm">
                    <span className="text-white font-semibold">{t('solutionsStore.customCta.implementationFee')}</span>
                  </div>
                  <div className="hidden sm:block w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="text-white/60 text-sm">
                    <span className="text-white font-semibold">{t('solutionsStore.customCta.monthlyFee')}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/custom-solutions')}
                  className="relative group/btn bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/60 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {t('solutionsStore.customCta.button')}
                    <svg className="w-6 h-6 ml-2 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Portfolio;
